import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, getConfig, type EventFeature, type EventProps, type ShopPlaceFeature } from '../api';
import { EventCard } from '../components/EventCard';
import { applyLocalFilters, FilterChips, type LocalFilters } from '../components/FilterChips';
import { PlanRun } from '../components/PlanRun';
import { SearchBox, type Located } from '../components/SearchBox';
import { SponsorCard } from '../components/SponsorCard';
import { SubscribeBox } from '../components/SubscribeBox';
import { Brand } from '../components/TopBar';
import { dayLabel, typesForGroups } from '../format';
import { MapView, type ViewChange } from '../map/MapView';
import { haversineMi, type Pt } from '../map/route';
import { navigate } from '../router';
import {
  readState,
  saveLastList,
  storedRadius,
  storeRadius,
  writeState,
  type MapState,
} from '../state/url-state';
import { setSignedIn as setVisitedSignedIn } from '../state/visited';

const ZOOM_FOR: Record<Located['kind'], number> = { zip: 11, place: 11, address: 14, device: 12 };
/** Past this zoom the visible area is small enough to search it automatically. */
const AUTO_BBOX_ZOOM = 8;

function zoomForRadius(r: number) {
  return r <= 5 ? 12 : r <= 10 ? 11 : r <= 25 ? 9.6 : r <= 50 ? 8.6 : r <= 75 ? 8 : 7.6;
}

export function Home() {
  const [state, setState] = useState<MapState>(() => {
    const s = readState(location.search);
    return location.search.includes('r=') ? s : { ...s, r: storedRadius() };
  });
  const [styleUrl, setStyleUrl] = useState<string | null>(null);
  // Set when the map can't start (no WebGL): search and the list still work without it.
  const [mapDown, setMapDown] = useState(false);
  const [features, setFeatures] = useState<EventFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [local, setLocal] = useState<LocalFilters>({ openBy8: false, hasPhotos: false });
  const [origin, setOrigin] = useState<Located | null>(null);
  const [routeOrder, setRouteOrder] = useState<string[] | null>(null);
  const [target, setTarget] = useState<{ lat: number; lng: number; z: number; key: number } | null>(
    null,
  );
  const [pendingView, setPendingView] = useState<ViewChange | null>(null);
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null);
  const [shopFeatures, setShopFeatures] = useState<ShopPlaceFeature[]>([]);
  const [mapBbox, setMapBbox] = useState<[number, number, number, number] | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const lastSearch = useRef<{ lat: number; lng: number; z: number } | null>(null);

  useEffect(() => {
    document.title = 'The Yard Sale Map — find yard sales near you';
    getConfig()
      .then((c) => setStyleUrl(c.styleUrl))
      .catch(() => setLoadErr('The map failed to load. Refresh to try again.'));
  }, []);

  useEffect(() => {
    api.get<{ user: unknown }>('/me').then((r) => {
      setSignedIn(r.user !== null);
      setVisitedSignedIn(r.user !== null);
    });
  }, []);

  // keep the URL in sync so every view is shareable
  useEffect(() => {
    history.replaceState(null, '', location.pathname + writeState(state));
    if (state.active) saveLastList(location.pathname + location.search);
  }, [state]);

  // fetch whenever the search inputs change
  useEffect(() => {
    if (!state.active) return;
    if (state.mode === 'bbox' && !bbox) return;
    const p = new URLSearchParams();
    if (state.mode === 'radius') {
      p.set('lat', String(state.lat));
      p.set('lng', String(state.lng));
      p.set('radius_mi', String(state.r));
    } else {
      p.set('bbox', bbox!.map((n) => n.toFixed(5)).join(','));
    }
    if (state.date) p.set('date', state.date);
    const ctl = new AbortController();
    const t = setTimeout(() => {
      setLoading(true);
      setLoadErr('');
      api
        .get<{ features: EventFeature[] }>(`/events?${p.toString()}`)
        .then((fc) => {
          if (!ctl.signal.aborted) {
            setFeatures(fc.features);
            setRouteOrder(null);
          }
        })
        .catch(
          () =>
            !ctl.signal.aborted &&
            setLoadErr("Couldn't load sales. Check your connection and try again."),
        )
        .finally(() => !ctl.signal.aborted && setLoading(false));
    }, 250);
    return () => {
      ctl.abort();
      clearTimeout(t);
    };
  }, [state.active, state.mode, state.lat, state.lng, state.r, state.date, bbox]);

  useEffect(() => {
    if (!state.active || !mapBbox) {
      setShopFeatures([]);
      return;
    }
    const ctl = new AbortController();
    const t = setTimeout(() => {
      api
        .get<{ features: ShopPlaceFeature[] }>(
          `/shop-places?bbox=${mapBbox.map((n) => n.toFixed(5)).join(',')}`,
        )
        .then((fc) => !ctl.signal.aborted && setShopFeatures(fc.features))
        .catch(() => {});
    }, 300);
    return () => {
      ctl.abort();
      clearTimeout(t);
    };
  }, [state.active, mapBbox]);

  const shopStops = useMemo(
    () =>
      shopFeatures.map((f) => ({
        id: f.properties.public_id,
        name: f.properties.name,
        lat: f.geometry.coordinates[1],
        lng: f.geometry.coordinates[0],
      })),
    [shopFeatures],
  );
  const shopIds = useMemo(
    () => new Set(shopFeatures.map((f) => f.properties.public_id)),
    [shopFeatures],
  );
  const shopNumbers = useMemo(() => {
    const m = new Map<string, number>();
    if (!routeOrder) return m;
    routeOrder.forEach((id, i) => {
      if (shopIds.has(id)) m.set(id, i + 1);
    });
    return m;
  }, [routeOrder, shopIds]);

  const allProps = useMemo(() => features.map((f) => f.properties), [features]);

  // type + local filters, then ordering (route order > distance/start)
  const visible: EventProps[] = useMemo(() => {
    const types = typesForGroups(state.types);
    const list = applyLocalFilters(
      types.length ? allProps.filter((e) => types.includes(e.event_type)) : allProps,
      local,
    );
    if (routeOrder) {
      const rank = new Map(routeOrder.map((id, i) => [id, i]));
      return [...list].sort(
        (a, b) => (rank.get(a.public_id) ?? 1e9) - (rank.get(b.public_id) ?? 1e9),
      );
    }
    return list;
  }, [allProps, state.types, local, routeOrder]);

  const visibleFeatures = useMemo(() => {
    const ids = new Set(visible.map((e) => e.public_id));
    return features.filter((f) => ids.has(f.properties.public_id));
  }, [features, visible]);

  const numbers = useMemo(() => {
    const m = new Map<string, number>();
    if (routeOrder) {
      routeOrder.forEach((id, i) => {
        if (!shopIds.has(id)) m.set(id, i + 1);
      });
      return m;
    }
    let n = 0;
    for (const e of visible) if (!e.is_online_only && !e.approximate) m.set(e.public_id, ++n);
    return m;
  }, [visible, routeOrder, shopIds]);

  const routeStops = useMemo(
    () =>
      visibleFeatures
        .filter((f) => f.geometry && !f.properties.is_online_only && !f.properties.approximate)
        .map((f) => ({
          id: f.properties.public_id,
          title: f.properties.title,
          lng: f.geometry!.coordinates[0],
          lat: f.geometry!.coordinates[1],
        })),
    [visibleFeatures],
  );

  const onLocated = useCallback(
    (l: Located) => {
      if (l.kind === 'device' || l.kind === 'address') setOrigin(l);
      const z =
        l.kind === 'address' || l.kind === 'device'
          ? Math.min(ZOOM_FOR[l.kind], zoomForRadius(state.r) + 2)
          : zoomForRadius(state.r);
      lastSearch.current = { lat: l.lat, lng: l.lng, z };
      setPendingView(null);
      setTarget({ lat: l.lat, lng: l.lng, z, key: Date.now() });
      setState((s) => ({ ...s, lat: l.lat, lng: l.lng, z, mode: 'radius', active: true }));
    },
    [state.r],
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const onViewChange = useCallback((c: ViewChange) => {
    setMapBbox(c.bbox);
    const s = stateRef.current;
    // From the initial national view, start searching the visible area once zoomed in.
    if (!s.active && c.userInitiated && c.z >= AUTO_BBOX_ZOOM) {
      lastSearch.current = { lat: c.lat, lng: c.lng, z: c.z };
      setBbox(c.bbox);
      setState({ ...s, lat: c.lat, lng: c.lng, z: c.z, mode: 'bbox', active: true });
      return;
    }
    setState((prev) => ({ ...prev, z: c.z, ...(prev.active ? {} : { lat: c.lat, lng: c.lng }) }));
    const last = lastSearch.current;
    if (c.userInitiated && last) {
      const moved = haversineMi(last, c) > 2 || Math.abs(c.z - last.z) > 1.2;
      setPendingView(moved ? c : null);
    }
  }, []);

  const searchThisArea = () => {
    if (!pendingView) return;
    lastSearch.current = { lat: pendingView.lat, lng: pendingView.lng, z: pendingView.z };
    setBbox(pendingView.bbox);
    setState((s) => ({
      ...s,
      lat: pendingView.lat,
      lng: pendingView.lng,
      z: pendingView.z,
      mode: 'bbox',
      active: true,
    }));
    setPendingView(null);
  };

  const hiddenCount = useMemo(() => visible.filter((e) => e.approximate).length, [visible]);
  const showDay = useMemo(
    () => new Set(visible.map((e) => dayLabel(e.start_at, e.timezone))).size > 1,
    [visible],
  );

  async function shareMap() {
    setSharing(true);
    setShared(false);
    try {
      const destination = location.pathname + location.search; // kept in sync by the effect above
      const { url } = await api.postJson<{ code: string; url: string }>('/share-links', {
        destination_path: destination,
      });
      if (navigator.share) await navigator.share({ title: 'Yard sales near you', url });
      else {
        await navigator.clipboard.writeText(url);
        setShared(true);
      }
    } catch {
      /* dismissed, or the request failed -- either way, just leave the button as-is */
    } finally {
      setSharing(false);
    }
  }

  const title = !state.active
    ? 'Find sales near you'
    : loading && !features.length
      ? 'Looking for sales…'
      : `${visible.length} ${visible.length === 1 ? 'listing' : 'listings'}${
          state.date === 'weekend'
            ? ' this weekend'
            : state.date === 'today'
              ? ' today'
              : state.date === 'tomorrow'
                ? ' tomorrow'
                : ''
        }`;

  return (
    <div className="home">
      <header className="home-head">
        <Brand />
        <SearchBox
          radius={state.r}
          onRadius={(r) => {
            storeRadius(r);
            setState((s) => ({ ...s, r, mode: 'radius' }));
            if (state.active && state.mode === 'radius')
              setTarget({ lat: state.lat, lng: state.lng, z: zoomForRadius(r), key: Date.now() });
          }}
          onLocated={onLocated}
        />
        <nav aria-label="Site">
          <a href="/how-it-works">How it works</a>
          <a href="/about">About</a>
        </nav>
        <a
          className="btn primary pill"
          href="/submit"
          style={{ marginLeft: 'auto', minHeight: 38, fontSize: '0.875rem' }}
        >
          List a sale
        </a>
      </header>
      <div className="home-body">
        <div className={mapDown ? 'mapwrap map-off' : 'mapwrap'}>
          {mapDown && (
            <p className="note" role="status">
              The map can't load in this browser (it needs WebGL). Search and the list of sales still work.
            </p>
          )}
          {styleUrl && !mapDown && (
            <MapView
              styleUrl={styleUrl}
              initial={{ lat: state.lat, lng: state.lng, z: state.z }}
              features={visibleFeatures}
              numbers={numbers}
              shopFeatures={shopFeatures}
              shopNumbers={shopNumbers}
              origin={origin}
              onViewChange={onViewChange}
              onSelect={(id) => navigate(`/e/${id}`)}
              target={target}
              onUnavailable={() => setMapDown(true)}
            />
          )}
          {pendingView && (
            <button className="search-area" onClick={searchThisArea}>
              Search this area
            </button>
          )}
          {loadErr && (
            <p className="note map-error" role="alert">
              {loadErr}
            </p>
          )}
        </div>
        <section className="sheet" aria-label="Listings">
          <div className="sheet-head">
            <div className="sheet-title" aria-live="polite">
              <h2>{title}</h2>
              {state.active && visible.length > 0 && hiddenCount > 0 && (
                <span className="sub">{hiddenCount} with address coming later</span>
              )}
              {state.active && (
                <button type="button" className="link" disabled={sharing} onClick={() => void shareMap()}>
                  {shared ? 'Link copied!' : sharing ? 'Sharing…' : 'Share this map'}
                </button>
              )}
            </div>
            <FilterChips
              types={state.types}
              date={state.date}
              local={local}
              all={allProps}
              onTypes={(types) => setState((s) => ({ ...s, types }))}
              onDate={(date) => setState((s) => ({ ...s, date }))}
              onLocal={setLocal}
            />
          </div>
          <div className="sheet-scroll">
            {state.active && visible.length > 0 && (
              <PlanRun
                stops={routeStops}
                hiddenCount={hiddenCount}
                shopStops={shopStops}
                defaultOrigin={origin}
                signedIn={signedIn}
                onOrdered={(ids, o: Pt | null) => {
                  setRouteOrder(ids);
                  if (o && !origin) setOrigin({ ...o, label: 'your start', kind: 'address' });
                }}
              />
            )}
            {/* Three mutually exclusive states, in one chain so they cannot overlap: nothing
                searched yet, searched and empty, or a list. They used to be two independent
                conditionals, which on first load rendered an <ol> holding nothing but the
                newsletter signup *above* the intro -- so a first-time visitor was asked to
                subscribe before being told what the site was. */}
            {!state.active ? (
              <>
                <div className="empty">
                  <h3>Free to browse. Free to list.</h3>
                  <p>
                    Yard, estate and church sales, flea and farmers markets, auctions and community
                    events, on one map. Search a place or use your location.
                  </p>
                </div>
                <SubscribeBox />
              </>
            ) : !loading && visible.length === 0 ? (
              <div className="empty">
                <h3>Nothing listed here yet</h3>
                <p>Try a bigger radius or clear a filter. Hosting something? It's free to list.</p>
                <a className="btn primary" href="/submit">
                  List a sale
                </a>
              </div>
            ) : (
              <ol className="results">
                {visible.map((e, i) => (
                  <Fragment key={e.public_id}>
                    {/* the top sponsored slot sits above the first result -- its own <li>, labeled, never
                        one of the ranked events (spec §39) */}
                    {i === 0 && <SponsorCard slot="top" />}
                    <EventCard ev={e} num={numbers.get(e.public_id)} showDay={showDay} />
                    {/* one sponsored card after the third result -- a separate <li>, never part
                        of the ranked events (spec §39: a sale can't rank higher for paying) */}
                    {i === 2 && <SponsorCard slot="second" />}
                  </Fragment>
                ))}
                {visible.length < 3 && <SponsorCard slot="second" />}
                <li className="results-subscribe">
                  <SubscribeBox />
                </li>
              </ol>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
